import React, { useMemo, useState } from 'react';
import { FormControl, Autocomplete } from '@mui/material';
import PropTypes from 'prop-types';
import ITAutocomplete from '@ui/Components/ITAutocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { constant } from '@config/BaseSetting';
import useLocalStorage from '@hooks/useLocalStorage';
import {
  getOrganizationFilterListAsync,
  resetOrganizationFilterListState,
} from '@features/organizationList/getOrganizationFilterListSlice';

const SEARCH_DELAY_MS = 500;

export default function OrganizationAutocompleteSection({
  partnerId,
  value,
  onChange,
  disabled,
  hasError,
}) {
  const [searchTimeoutHandle, setSearchTimeoutHandle] = useState();
  const dispatch = useDispatch();
  const [accessTokenSet] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);
  const { getOrganizationFilterListState } = useSelector((state) => state.organizationFilterList);
  const { fetching, organizationFilterList } = getOrganizationFilterListState;

  const fetchOrganizationFilter = (search) => {
    dispatch(
      getOrganizationFilterListAsync({
        accessToken: accessTokenSet,
        reset: true,
        ...{ searchTerm: search, partnerId: partnerId ?? undefined },
      })
    );
  };

  const handleOpen = () => {
    fetchOrganizationFilter('');
  };

  const handleClose = () => {
    dispatch(resetOrganizationFilterListState());
  };

  const handleSearchChange = (searchText, open) => {
    clearTimeout(searchTimeoutHandle);
    if (!open) return;
    const newHandle = setTimeout(() => fetchOrganizationFilter(searchText), SEARCH_DELAY_MS);
    setSearchTimeoutHandle(newHandle);
  };

  const options = useMemo(() =>
    organizationFilterList.map((item) => ({
      id: item?.id,
      value: item?.id,
      title: `${item?.id} - ${item.organizationName}`,
    }))
  );

  return (
    <ITAutocomplete
      options={options}
      value={value}
      onChange={onChange}
      placeholder="Select Organization"
      loading={fetching}
      onOpen={handleOpen}
      onClose={handleClose}
      onSearchChange={handleSearchChange}
      getOptionLabel={(option) => option.title}
      getOptionIdentifier={(option) => option.id}
      filterOptions={false}
      disabled={disabled}
      hasError={hasError}
    />
  );
}

OrganizationAutocompleteSection.propTypes = {
  partnerId: PropTypes.number,
  value: PropTypes.any,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
};

OrganizationAutocompleteSection.defaultProps = {
  partnerId: 0,
  value: null,
  onChange: () => {},
  disabled: false,
  hasError: false,
};
